/**
 * useProcessReorder — live "reordered" conformance for the displayed process.
 *
 * Reads the observed orderings from the mounted feed ({@link useLiveFlows}) and,
 * given the process's walker steps, returns which steps ran out of order plus
 * the trace tallies. Degrades to an empty result with no provider (the viewer)
 * or no telemetry — so the process view highlights nothing unless drift is live.
 */
import { useMemo } from "react";
import { useLiveFlows } from "./liveFlowsStore.js";
import { modeledStepSeq, analyzeReorder, type ReorderResult, type StepLike } from "./processConformance.js";

const EMPTY: ReorderResult = { reorderedSteps: new Set(), reorderedTraces: 0, conformingTraces: 0 };

export function useProcessReorder(steps: readonly StepLike[] | undefined): ReorderResult {
  const flows = useLiveFlows();
  return useMemo(() => {
    if (!steps || steps.length === 0 || flows.length === 0) return EMPTY;
    return analyzeReorder(modeledStepSeq(steps), flows);
  }, [steps, flows]);
}
